import { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { fluidRange } from 'polished';
import { AnimatePresence, motion } from 'framer-motion';
import styled from '@happymoneyinc/styled-components';
import ThemeWrapper from '../utils/ThemeWrapper/index.js';
import { focus } from '../utils/global-styles.js';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import SvgIcon from '../SvgIcon/SvgIcon.js';
import Loader from '../Loader/Loader.js';
import Link from '../Link/Link.js';
import Button from '../Button/Button.js';

const Modal = props => {
  var _modalChildrenRef$cur;
  const {
    children,
    className,
    isLoading,
    isOpen,
    onClose,
    primaryBtnProps,
    secondaryBtnProps,
    tertiaryBtnProps,
    testID,
    maxWidth,
    backdropBackground,
    closeBtnProps
  } = props;
  const modalChildrenRef = useRef(null);
  const escFunction = e => {
    if (e.keyCode === 27 && isOpen) {
      onClose();
    }
  };
  useEffect(() => {
    if (document !== undefined && isOpen) {
      document.addEventListener('keydown', escFunction, false);
    }
    return () => {
      document.removeEventListener('keydown', escFunction, false);
    };
  });
  return /*#__PURE__*/jsx(ThemeWrapper, {
    children: /*#__PURE__*/jsx(AnimatePresence, {
      children: isOpen && /*#__PURE__*/jsxs(Fragment, {
        children: [/*#__PURE__*/jsx(ModalContainer, {
          className: className,
          "data-testid": testID,
          initial: {
            y: 70,
            opacity: 0
          },
          animate: {
            y: 0,
            opacity: 1
          },
          transition: {
            duration: 0.3,
            ease: 'easeOut'
          },
          exit: {
            opacity: 0
          },
          children: /*#__PURE__*/jsxs(ModalInner, {
            $maxWidth: maxWidth,
            children: [/*#__PURE__*/jsx(CloseButton, {
              onClick: onClose,
              element: "button",
              ...closeBtnProps,
              children: /*#__PURE__*/jsx(SvgIcon, {
                type: "close"
              })
            }), isLoading && /*#__PURE__*/jsx(LoaderContainer, {
              tempModalHeight: modalChildrenRef === null || modalChildrenRef === void 0 ? void 0 : (_modalChildrenRef$cur = modalChildrenRef.current) === null || _modalChildrenRef$cur === void 0 ? void 0 : _modalChildrenRef$cur.scrollHeight,
              children: /*#__PURE__*/jsx(Loader, {})
            }), !isLoading && /*#__PURE__*/jsxs(ModalChildren, {
              ref: modalChildrenRef,
              children: [/*#__PURE__*/jsx(ContentContainer, {
                children: children
              }), (primaryBtnProps || secondaryBtnProps || tertiaryBtnProps) && /*#__PURE__*/jsxs(ButtonsContainer, {
                children: [primaryBtnProps && /*#__PURE__*/jsx(StyledButton, {
                  disabled: primaryBtnProps.disabled,
                  onClick: primaryBtnProps.onClick,
                  type: primaryBtnProps.type,
                  testID: primaryBtnProps.testID,
                  children: primaryBtnProps.buttonText
                }), secondaryBtnProps && /*#__PURE__*/jsx(StyledButton, {
                  outlined: true,
                  onClick: secondaryBtnProps.onClick,
                  testID: secondaryBtnProps.testID,
                  children: secondaryBtnProps.buttonText
                }), tertiaryBtnProps && /*#__PURE__*/jsx(StyledLink, {
                  onClick: tertiaryBtnProps.onClick,
                  testID: tertiaryBtnProps.testID,
                  children: tertiaryBtnProps.buttonText
                })]
              })]
            })]
          })
        }), /*#__PURE__*/jsx(ModalBackdrop, {
          initial: {
            opacity: 0
          },
          animate: {
            opacity: 1
          },
          transition: {
            duration: 0.2
          },
          exit: {
            opacity: 0
          },
          background: backdropBackground
        })]
      })
    })
  });
};
const LoaderContainer = styled.div`
  display: flex;
  height: ${_ref => {
  let {
    tempModalHeight
  } = _ref;
  return `${tempModalHeight}px`;
}};
`;
const ModalContainer = styled(motion.div)`
  position: fixed;
  align-items: flex-start;
  justify-content: center;
  z-index: ${_ref2 => {
  let {
    theme
  } = _ref2;
  return theme.globals.zIndex.modal;
}};
  display: flex;
  padding: ${_ref3 => {
  let {
    theme
  } = _ref3;
  return theme.spacing[7];
}} 0 ${_ref4 => {
  let {
    theme
  } = _ref4;
  return theme.spacing[7];
}} 0;
  margin: ${_ref5 => {
  let {
    theme
  } = _ref5;
  return `0 ${theme.spacing[3]}`;
}};
  overflow-y: auto;
  inset: 0;
`;
const ModalInner = styled.div`
  position: relative;
  width: 100%;
  max-width: ${_ref6 => {
  let {
    $maxWidth
  } = _ref6;
  return $maxWidth;
}};
  background: ${_ref7 => {
  let {
    theme
  } = _ref7;
  return theme.colors.white;
}};
  box-shadow: 12px 12px rgb(90 88 89 / 25%);
  border: 2px solid ${_ref8 => {
  let {
    theme
  } = _ref8;
  return theme.colors.black;
}};
  border-radius: 2px;
`;
const ModalChildren = styled.div`
  padding: ${_ref9 => {
  let {
    theme
  } = _ref9;
  return theme.spacing[8];
}} ${_ref10 => {
  let {
    theme
  } = _ref10;
  return theme.spacing[5];
}} ${_ref11 => {
  let {
    theme
  } = _ref11;
  return theme.spacing[5];
}} ${_ref12 => {
  let {
    theme
  } = _ref12;
  return theme.spacing[5];
}};
  background: ${_ref13 => {
  let {
    theme
  } = _ref13;
  return theme.colors.white;
}};
`;
const CloseButton = styled(Link)`
  ${focus}

  position: absolute;
  top: 20px;
  right: 20px;
`;
const ContentContainer = styled.div`
  text-align: center;
`;
const ButtonsContainer = styled.div`
  margin-top: 56px;
`;
const StyledButton = styled(Button)`
  width: 100%;
  margin-top: ${_ref14 => {
  let {
    theme
  } = _ref14;
  return theme.spacing[2];
}};
`;
const StyledLink = styled(Link)`
  display: block;
  margin: ${_ref15 => {
  let {
    theme
  } = _ref15;
  return `${theme.spacing[5]} 0 0 0`;
}};
  color: ${_ref16 => {
  let {
    theme
  } = _ref16;
  return theme.colors.black;
}};
  font-family: ${_ref17 => {
  let {
    theme
  } = _ref17;
  return theme.fonts.MabryProRegular;
}};
  ${_ref18 => {
  let {
    theme
  } = _ref18;
  return fluidRange({
    prop: 'font-size',
    fromSize: `${theme.fontStyles.buttons.primary.fontSizeMin}px`,
    toSize: `${theme.fontStyles.buttons.primary.fontSizeMax}px`
  }, `${theme.screen.xs}`, `${theme.screen.xl}`);
}}

  -webkit-font-smoothing: antialiased;
  text-align: center;
  text-decoration: none;
`;
const ModalBackdrop = styled(motion.div)`
  position: fixed;
  z-index: ${_ref19 => {
  let {
    theme
  } = _ref19;
  return theme.globals.zIndex.overlay;
}};
  display: block;
  background: ${_ref20 => {
  let {
    background
  } = _ref20;
  return `${background}`;
}};
  inset: 0;
`;
Modal.defaultProps = {
  children: null,
  className: '',
  isLoading: false,
  isOpen: false,
  onClose: null,
  primaryBtnProps: null,
  secondaryBtnProps: null,
  tertiaryBtnProps: null,
  closeBtnProps: null,
  testID: 'modal',
  maxWidth: '535px',
  backdropBackground: 'rgba(36, 34, 32, 0.38)'
};
Modal.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  /** @ignore */
  className: PropTypes.string,
  onClose: PropTypes.func,
  /** If true shows a loader inside the open modal while some other action takes place */
  isLoading: PropTypes.bool,
  isOpen: PropTypes.bool,
  primaryBtnProps: PropTypes.shape({
    buttonText: PropTypes.string,
    disabled: PropTypes.bool,
    onClick: PropTypes.func,
    type: PropTypes.string,
    testID: PropTypes.string
  }),
  secondaryBtnProps: PropTypes.shape({
    buttonText: PropTypes.string,
    onClick: PropTypes.func,
    testID: PropTypes.string
  }),
  tertiaryBtnProps: PropTypes.shape({
    buttonText: PropTypes.string,
    onClick: PropTypes.func,
    testID: PropTypes.string
  }),
  closeBtnProps: PropTypes.shape({
    disabled: PropTypes.bool,
    onClick: PropTypes.func,
    testID: PropTypes.string
  }),
  testID: PropTypes.string,
  maxWidth: PropTypes.string,
  backdropBackground: PropTypes.string
};
var Modal$1 = Modal;

export { Modal$1 as default };
//# sourceMappingURL=Modal.js.map
